import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from '../../contexts/ToastContext';
import AuthService from '../../services/authService';
import UserService from '../../services/userService';
import Drawer from '../common/Drawer';
import './AddUserModal.css';

const AddUserModal = ({ isOpen, onClose, onUserCreated }) => {
  const { showSuccess, showError } = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    getValues,
    setError,
    clearErrors,
    setFocus,
  } = useForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ isValid: false, errors: [] });
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [usernameCheckTimeout, setUsernameCheckTimeout] = useState(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(false);

  const watchPassword = watch('password', '');
  const watchUsername = watch('username', '');

  // Focus the first field once the drawer has opened. Escape, the focus
  // trap and the body scroll lock are handled by <Drawer>.
  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => setFocus('firstname'), 100);
    return () => clearTimeout(timer);
  }, [isOpen, setFocus]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      reset();
      setFieldErrors({});
      setPasswordStrength({ isValid: false, errors: [] });
      setShowPassword(false);
    }
  }, [isOpen, reset]);

  // Check password strength
  useEffect(() => {
    if (watchPassword) {
      const strength = AuthService.validatePassword(watchPassword);
      setPasswordStrength(strength);
    } else {
      setPasswordStrength({ isValid: false, errors: [] });
    }
  }, [watchPassword]);

  // Check username uniqueness with debounce
  useEffect(() => {
    if (watchUsername && watchUsername.length >= 3) {
      clearTimeout(usernameCheckTimeout);
      setUsernameAvailable(false);
      
      const newTimeout = setTimeout(async () => {
        setIsCheckingUsername(true);
        try {
          const result = await UserService.checkUsernameUnique(watchUsername);
          if (result.success) {
            if (!result.isUnique) {
              setError('username', {
                type: 'manual',
                message: 'Username is already taken'
              });
              setUsernameAvailable(false);
            } else {
              clearErrors('username');
              setUsernameAvailable(true);
            }
          }
        } catch (error) {
          console.error('Error checking username:', error);
          setUsernameAvailable(false);
        } finally {
          setIsCheckingUsername(false);
        }
      }, 500);
      
      setUsernameCheckTimeout(newTimeout);
      return () => clearTimeout(newTimeout);
    } else {
      setUsernameAvailable(false);
    }
  }, [watchUsername, usernameCheckTimeout, setError, clearErrors]);

  const validateEmail = (email) => {
    return AuthService.validateEmail(email) || 'Please enter a valid email address';
  };

  const validatePasswordMatch = (confirmPassword) => {
    const { password } = getValues();
    return password === confirmPassword || 'Passwords do not match';
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setFieldErrors({});

    const userData = {
      firstname: data.firstname.trim(),
      lastname: data.lastname.trim(),
      email: data.email.trim().toLowerCase(),
      username: data.username.trim(),
      password: data.password,
      user_type: data.userType
    };

    try {
      const result = await UserService.createUser(userData);

      if (result.success) {
        showSuccess('User created successfully');
        
        onUserCreated(result.data);
        
        reset();
        setFieldErrors({});
        
        onClose();
      } else {
        if (result.fieldErrors) {
          setFieldErrors(result.fieldErrors);
          Object.keys(result.fieldErrors).forEach(field => {
            setError(field, {
              type: 'manual',
              message: result.fieldErrors[field]
            });
          });
          showError('Please fix the errors in the form');
        } else {
          setFieldErrors({ general: result.error });
          showError(result.error || 'Failed to create user');
        }
      }
    } catch (error) {
      console.error('Unexpected error creating user:', error);
      const errorMessage = 'An unexpected error occurred. Please try again.';
      setFieldErrors({ general: errorMessage });
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <>
      <button type="button" className="app-drawer-btn app-drawer-btn--secondary" onClick={onClose} disabled={isSubmitting}>
        Cancel
      </button>
      <button type="submit" className="app-drawer-btn app-drawer-btn--primary" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <span className="spinner" aria-hidden="true"></span>
            <span>Creating User...</span>
          </>
        ) : (
          'Create User'
        )}
      </button>
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Add New User"
      footer={footer}
      className="add-user-drawer"
    >
          <form onSubmit={handleSubmit(onSubmit)} className="add-user-form" noValidate>
          {fieldErrors.general && (
            <div className="error-message" role="alert">
              {fieldErrors.general}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="firstname">
              First Name <span className="required">*</span>
            </label>
            <input
              id="firstname"
              type="text"
              className={`form-input ${errors.firstname ? 'error' : ''}`}
              placeholder="Enter first name"
              {...register('firstname', {
                required: 'Full name is required',
                minLength: {
                  value: 2,
                  message: 'First name must be at least 2 characters'
                },
                maxLength: {
                  value: 100,
                  message: 'First name must be less than 100 characters'
                }
              })}
              aria-describedby={errors.firstname ? 'name-error' : undefined}
              aria-invalid={errors.firstname ? 'true' : 'false'}
            />
            {errors.firstname && (
              <span id="name-error" className="field-error" role="alert">
                {errors.firstname.message}
              </span>
            )}
          </div>

          <div className="form-group">
              <label htmlFor="lastname">
                  Last Name <span className="required">*</span>
              </label>
              <input
                  id="lastname"
                  type="text"
                  className={`form-input ${errors.lastname ? 'error' : ''}`}
                  placeholder="Enter full name"
                  {...register('lastname', {
                      required: 'Last name is required',
                      minLength: {
                          value: 2,
                          message: 'Last name must be at least 2 characters'
                      },
                      maxLength: {
                          value: 100,
                          message: 'Last name must be less than 100 characters'
                      }
                  })}
                  aria-describedby={errors.lastname ? 'name-error' : undefined}
                  aria-invalid={errors.lastname ? 'true' : 'false'}
              />
              {errors.lastname && (
                  <span id="name-error" className="field-error" role="alert">
            {errors.lastname.message}
          </span>
              )}
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email Address <span className="required">*</span>
            </label>
            <input
              id="email"
              type="email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder="example@company.com"
              {...register('email', {
                required: 'Email is required',
                validate: validateEmail
              })}
              aria-describedby={errors.email ? 'email-error' : 'email-helper'}
              aria-invalid={errors.email ? 'true' : 'false'}
            />
            {!errors.email && (
              <span id="email-helper" className="field-helper">Enter a valid email address</span>
            )}
            {errors.email && (
              <span id="email-error" className="field-error" role="alert">
                {errors.email.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="username">
              Username <span className="required">*</span>
            </label>
            <div className="input-with-status">
              <input
                id="username"
                type="text"
                className={`form-input ${errors.username ? 'error' : usernameAvailable && watchUsername ? 'valid' : ''}`}
                placeholder="Enter username (3-30 characters)"
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Username must be at least 3 characters'
                  },
                  maxLength: {
                    value: 30,
                    message: 'Username must be less than 30 characters'
                  },
                  pattern: {
                    value: /^[A-Za-z0-9_-]+$/,
                    message: 'Username can only contain letters, numbers, underscores, and hyphens'
                  }
                })}
                aria-describedby={errors.username ? 'username-error' : 'username-helper'}
                aria-invalid={errors.username ? 'true' : 'false'}
              />
              {isCheckingUsername && (
                <span className="input-status checking">
                  <span className="status-spinner"></span>
                  Checking...
                </span>
              )}
              {!isCheckingUsername && usernameAvailable && watchUsername && !errors.username && (
                <span className="input-status available">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="status-icon">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Available
                </span>
              )}
            </div>
            {!errors.username && !usernameAvailable && (
              <span id="username-helper" className="field-helper">Use letters, numbers, underscores or hyphens</span>
            )}
            {errors.username && (
              <span id="username-error" className="field-error" role="alert">
                {errors.username.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password <span className="required">*</span>
            </label>
            <div className="password-input-container">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`form-input ${errors.password ? 'error' : ''}`}
                placeholder="Create a strong password"
                {...register('password', {
                  required: 'Password is required',
                  validate: (value) => passwordStrength.isValid || passwordStrength.errors[0]
                })}
                aria-describedby="password-strength password-error"
                aria-invalid={errors.password ? 'true' : 'false'}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 20 20" fill="currentColor" className="toggle-icon">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 20 20" fill="currentColor" className="toggle-icon">
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                )}
              </button>
            </div>
            {watchPassword && (
              <div id="password-strength" className="password-strength">
                <div className="strength-meter-container">
                  <div 
                    className="strength-meter" 
                    style={{
                      width: `${calculatePasswordStrength(passwordStrength)}%`,
                      backgroundColor: getPasswordStrengthColor(passwordStrength)
                    }}
                  ></div>
                </div>
                <div className={`strength-indicator ${passwordStrength.isValid ? 'strong' : 'weak'}`}>
                  {passwordStrength.isValid ? '✓ Strong password' : '⚠ Weak password'}
                </div>
                <div className="strength-requirements-container">
                  {passwordStrength.errors.map((error, index) => {
                    const isValid = !error.includes('must');
                    const requirement = error.replace('Password must ', '');
                    return (
                      <div key={index} className={`requirement ${isValid ? 'valid' : 'invalid'}`}>
                        {isValid ? (
                          <svg viewBox="0 0 20 20" fill="currentColor" className="requirement-icon">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 20 20" fill="currentColor" className="requirement-icon">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                          </svg>
                        )}
                        <span>{requirement}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {errors.password && (
              <span id="password-error" className="field-error" role="alert">
                {errors.password.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              Confirm Password <span className="required">*</span>
            </label>
            <input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              className={`form-input ${errors.confirmPassword ? 'error' : watchPassword && getValues('confirmPassword') === watchPassword ? 'valid' : ''}`}
              placeholder="Confirm your password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: validatePasswordMatch
              })}
              aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
              aria-invalid={errors.confirmPassword ? 'true' : 'false'}
            />
            {watchPassword && getValues('confirmPassword') === watchPassword && getValues('confirmPassword') && (
              <div className="password-match">
                <svg viewBox="0 0 20 20" fill="currentColor" className="match-icon">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Passwords match</span>
              </div>
            )}
            {errors.confirmPassword && (
              <span id="confirmPassword-error" className="field-error" role="alert">
                {errors.confirmPassword.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label>
              User Type <span className="required">*</span>
            </label>
            <div className="segmented-control" role="radiogroup" aria-labelledby="user-type-label">
              <label className={`segment-option ${watch('userType') === 'SUPER_ADMIN' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  value="SUPER_ADMIN"
                  {...register('userType', { required: 'User type is required' })}
                  className="segment-radio"
                />
                <div className="segment-content">
                  <div className="segment-icon">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      <path fillRule="evenodd" d="M13 6a3 3 0 11-6 0 3 3 0 016 0zm-1-3a2 2 0 104 0 2 2 0 00-4 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="segment-label">
                    <span className="segment-title">Super Admin</span>
                    <span className="segment-description">Full system access</span>
                  </div>
                </div>
              </label>
              <label className={`segment-option ${watch('userType') === 'ADMIN' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  value="ADMIN"
                  {...register('userType', { required: 'User type is required' })}
                  className="segment-radio"
                />
                <div className="segment-content">
                  <div className="segment-icon">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="segment-label">
                    <span className="segment-title">Admin</span>
                    <span className="segment-description">Manage users & content</span>
                  </div>
                </div>
              </label>
              <label className={`segment-option ${watch('userType') === 'STAFF' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  value="STAFF"
                  {...register('userType', { required: 'User type is required' })}
                  className="segment-radio"
                />
                <div className="segment-content">
                  <div className="segment-icon">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                  </div>
                  <div className="segment-label">
                    <span className="segment-title">Staff</span>
                    <span className="segment-description">Limited access</span>
                  </div>
                </div>
              </label>
            </div>
            {errors.userType && (
              <span className="field-error" role="alert">
                {errors.userType.message}
              </span>
            )}
          </div>

          </form>
    </Drawer>
  );
};

// Helper function to calculate password strength percentage
const calculatePasswordStrength = (passwordStrength) => {
  if (!passwordStrength) return 0;
  
  // Count how many requirements are met
  const totalRequirements = 5; // min length, uppercase, lowercase, number, special char
  const metRequirements = totalRequirements - passwordStrength.errors.length;
  
  return (metRequirements / totalRequirements) * 100;
};

// Helper function to get color based on password strength
const getPasswordStrengthColor = (passwordStrength) => {
  const percentage = calculatePasswordStrength(passwordStrength);
  
  if (percentage <= 20) return '#ef4444'; // Red (very weak)
  if (percentage <= 40) return '#f97316'; // Orange (weak)
  if (percentage <= 60) return '#eab308'; // Yellow (medium)
  if (percentage <= 80) return '#84cc16'; // Light green (strong)
  return '#10b981'; // Green (very strong)
};

export default AddUserModal;
